/**
 * DashboardPageHeader — Page heading for dashboard / builder / settings routes.
 *
 * Standardizes the page-title heading pattern across React SPA dashboards so
 * that every route has exactly one `<h1>` (a11y / SEO / document-outline
 * requirement) and the right-rail action cluster lays out consistently.
 *
 * Sister component to `<ListPageHeader>` (which is opinionated about list
 * counts + a single primary CTA). This component intentionally has no count
 * semantic — the right rail is a `ReactNode` slot so callers can compose any
 * shape: 1–N buttons, save/cancel pair, multi-step builder save, etc.
 *
 * ---------------------------------------------------------------------------
 * Heading-level contract (2026-06-23) — IMPORTANT for Phase 6 consumers
 * ---------------------------------------------------------------------------
 *
 * The `DashboardLayout` shell header shows the APP name (e.g. "Finance") as
 * a styled `<p>` — NOT an `<h1>`. This means DashboardPageHeader's `<h1>`
 * is the **sole h1** on every dashboard page. Maintain this invariant:
 *
 *   ✅ DashboardLayout app title → `<p>` (navigation chrome, not a heading)
 *   ✅ DashboardPageHeader title → `<h1>` (default) — the page's content h1
 *   ❌ Never add another `<h1>` inside a route component
 *
 * The `as` prop lets nested headers use `<h2>`/`<h3>` in contexts where
 * DashboardPageHeader appears inside a section that already has a route-level
 * h1 — for example a drawer-embedded sub-form or a nested settings panel.
 * Default is always `h1`; only override when a route-level h1 already exists
 * as a sibling ancestor.
 *
 * Phase 6 note (outreach SequenceBuilder, dgo PageHeader/Onboarding):
 *   Use `<DashboardPageHeader title="…" />` with default `as="h1"`. Do not
 *   place an h1 anywhere else in the route template. If a drawer-embedded
 *   sub-form needs a heading, use `as="h2"`.
 *
 * @layer widget
 * @package @plerity/dashboard-shared
 */
import { clsx } from "clsx";
import type { ElementType, ReactNode } from "react";

/** HTML heading element tags accepted by the `as` prop. */
export type HeadingLevel = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

export interface DashboardPageHeaderProps {
  /**
   * Required page title — rendered as the heading element specified by `as`.
   *
   * Default `as="h1"`. Only override when a route-level h1 already exists as
   * an ancestor — see the heading-level contract in this module's JSDoc.
   */
  title: string;
  /**
   * HTML heading element to use for the title.
   *
   * @default "h1"
   *
   * Heading-level contract: `DashboardLayout` renders the app name as `<p>`
   * so this `<h1>` is the page's sole h1. Use `as="h2"` only in nested
   * contexts (e.g. drawer sub-forms) where a parent route already owns h1.
   */
  as?: HeadingLevel;
  /** Optional supporting copy below the title. */
  subtitle?: string;
  /** Optional eyebrow text above the title (route taxonomy, app name, step indicator). */
  eyebrow?: string;
  /** Right-rail content — buttons, filter clusters, builder save action, etc. */
  actions?: ReactNode;
  /** Optional bottom-aligned content (tabs, progress nav, secondary controls). */
  belowTitle?: ReactNode;
  /** Optional className passthrough on the root <header>. */
  className?: string;
  /** Optional id for the rendered heading — useful for aria-labelledby on adjacent regions. */
  titleId?: string;
}

export function DashboardPageHeader({
  title,
  as: Heading = "h1" as HeadingLevel,
  subtitle,
  eyebrow,
  actions,
  belowTitle,
  className,
  titleId,
}: DashboardPageHeaderProps) {
  const HeadingTag = Heading as ElementType;
  return (
    <header className={clsx("space-y-4", className)}>
      {/*
        Responsive contract (FE-MOBILE, 2026-05-23):

        On `<sm` (mobile) the title and the action cluster STACK vertically
        — actions render BELOW the title on a fresh row with full viewport
        width. On `sm:+` they go side-by-side with the title growing and the
        actions sitting on the right.

        Previously the wrapper was a single flex row at every breakpoint
        with `shrink-0` on the actions column; that crushed the title's
        `min-w-0 flex-1` column to ~0 width whenever the action cluster
        was wider than the viewport (e.g. EmailOverview's 3 CTAs at 375px),
        producing letter-per-line subtitle text and a "N." (truncated to a
        single character + ellipsis) title. The stack + wrap pattern is
        the same recipe used by BrandSettings.tsx and the canonical
        `flex flex-wrap` cluster.
      */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 sm:flex-1">
          {eyebrow && <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted">{eyebrow}</p>}
          {/*
            `truncate` keeps the title on one line in the desktop row layout
            and ellipsizes when narrow; the native `title` attribute exposes
            the full text on hover so users with constrained widths can still
            see the full page name. On mobile (the stacked layout above) the
            title has the full viewport width so truncation is a no-op there.
            `break-words` on the subtitle lets longer supporting copy wrap
            instead of overflowing.
          */}
          <HeadingTag id={titleId} title={title} className="truncate text-2xl font-bold text-fg">
            {title}
          </HeadingTag>
          {subtitle && <p className="mt-1 text-sm text-muted break-words">{subtitle}</p>}
        </div>
        {actions && (
          // `flex-wrap` lets multi-button clusters break to a new row when
          // they exceed the available width on smaller screens before the
          // `sm` breakpoint kicks in (e.g. portrait tablets) and prevents
          // any single button from being clipped off-viewport.
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>
        )}
      </div>
      {belowTitle && <div>{belowTitle}</div>}
    </header>
  );
}
